import AppDesignPreview from './AppDesignPreview'
import { useAppDispatch } from '../../../hooks/StoreHooks'
import { useEffect, useState } from 'react'
import { fetchAppDesignThunk, updateAppDesignColorThunk, updateAppDesignLogoThunk } from '../../../store/thunk/configurationThunk'
import { useAppDesignAccentColor, useAppDesignCompanyLogo } from '../../../store/selectors/configurationSelector'
import { Box, Divider } from '@mui/material'
import { GeneralButton } from '../../../common/ui/GeneralButton'
import { useTranslation } from 'react-i18next'
import { AppDesignColor } from './AppDesignColor'
import { AppDesignLogo } from './AppDesignLogo'
export const AppDesignContainer = () => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();

	const savedLogoUrl = useAppDesignCompanyLogo();
	const savedAccentColor = useAppDesignAccentColor();

	const [isEditMode, setIsEditMode] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const [overrideLogo, setOverrideLogo] = useState(false);
	const [draftLogoUri, setDraftLogoUri] = useState<string | null>(null);
	const [draftLogoFile, setDraftLogoFile] = useState<File | null>(null);  // ← base64 → File

	const [overrideAccentColor, setOverrideAccentColor] = useState(false);
	const [draftAccentColor, setDraftAccentColor] = useState<string | null>(null);

	const fetchDataAnyApply = async () => {
		await dispatch(fetchAppDesignThunk());
	};

	useEffect(() => {
		if (!isEditMode) {
			setDraftLogoUri(savedLogoUrl ?? null);
			setDraftAccentColor(savedAccentColor ?? null);
		}
	}, [savedLogoUrl, savedAccentColor, isEditMode]);

	useEffect(() => {
		fetchDataAnyApply();
	}, []);


	const handleSelectedLogo = (dataUrl: string, file: File) => {
		setDraftLogoUri(dataUrl);
		setDraftLogoFile(file);
		setOverrideLogo(true);
	};

	const handleResetLogo = () => {
		setDraftLogoUri(savedLogoUrl ?? null);
		setDraftLogoFile(null);
		setOverrideLogo(false);
	};

	const handleDeleteLogo = () => {
		setDraftLogoUri(null);
		setDraftLogoFile(null);
		setOverrideLogo(savedLogoUrl != null);
	};

	const handleSelectColor = (colorHex: string) => {
		setDraftAccentColor(colorHex);
		setOverrideAccentColor(true);
	};

	const handleResetColor = () => {
		setDraftAccentColor(savedAccentColor ?? null);
		setOverrideAccentColor(false);
	};

	const handleDeleteColor = () => {
		setDraftAccentColor(null);
		setOverrideAccentColor(savedAccentColor != null);
	};

	// General
	const handleChangeMode = async () => {
		if (!isEditMode) {
			setIsEditMode(true);
		} else {
			setIsSaving(true);
			try {
				if (overrideAccentColor) {
					await dispatch(updateAppDesignColorThunk({ colorHex: draftAccentColor })).unwrap();
				}

				if (overrideLogo) {
					await dispatch(updateAppDesignLogoThunk(draftLogoFile)).unwrap();
				}

				await fetchDataAnyApply();

				setOverrideLogo(false);
				setOverrideAccentColor(false);
				setDraftLogoFile(null);

				setIsEditMode(false);
			} catch (e) {
				// Errors are handled and displayed globally
				// We catch to keep edit mode active
			} finally {
				setIsSaving(false);
			}
		}
	};

	const handleCancel = () => {
		setIsEditMode(false);
		setDraftLogoUri(savedLogoUrl ?? null);
		setDraftLogoFile(null);
		setDraftAccentColor(savedAccentColor ?? null);
		setOverrideAccentColor(false);
		setOverrideLogo(false);
	};

	const handleDelete = () => {
		setDraftLogoUri(null);
		setDraftLogoFile(null);
		setDraftAccentColor(null);
		setOverrideLogo(savedLogoUrl != null);
		setOverrideAccentColor(savedAccentColor != null);
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				padding: "2em",
				border: (theme) => `1px solid ${theme.palette.border?.main}`,
				background: (theme) => theme.palette.backgroundColor.main,
			}}
		>
			<Box sx={{ display: "flex", flexDirection: "row", gap: 10, alignItems: "flex-start", flexWrap: "wrap", justifyContent: "flex-start" }}>
				<AppDesignLogo
					isEditMode={isEditMode}
					isSaving={isSaving}
					logoUrl={draftLogoUri}
					onLogoSelected={handleSelectedLogo}
					onResetClick={handleResetLogo}
					onDeleteClick={handleDeleteLogo}
				/>

				<AppDesignPreview
					accentColor={draftAccentColor}
					companyLogoUrl={draftLogoUri}
				/>

				<AppDesignColor
					isEditMode={isEditMode}
					isSaving={isSaving}
					accentColor={draftAccentColor}
					onColorChange={handleSelectColor}
					onResetClick={handleResetColor}
					onDeleteClick={handleDeleteColor}
				/>
			</Box>

			<Divider />

			<Box sx={{ display: "flex", justifyContent: "flex-start", gap: 2 }}>
				<GeneralButton onClick={handleChangeMode} loading={isSaving}>
					{!isEditMode ? t("buttons.edit") : t("buttons.save")}
				</GeneralButton>
				{isEditMode && !isSaving && (
					<>
						<GeneralButton onClick={handleCancel} appVariant='secondary'>
							{t("buttons.cancel")}
						</GeneralButton>
						<GeneralButton onClick={handleDelete} appVariant='danger'>
							{t("buttons.delete")}
						</GeneralButton>
					</>
				)}
			</Box>
		</Box>
	);
};